import { useEffect, useRef } from 'react'

import { useLayoutContext } from '../../../context/layoutContext'
import { useTemplateContext } from '../../../context/templateContext'
import type { TemplateVersionInfo } from '../../../types/templateModel'
import { TemplateTopBar } from '../TemplateTopBar'
import { scrollUntilElementIsVisible } from './helpers'
import { HiddenTemplateViewer } from './HiddenTemplateViewer'
import { TemplateVersionViewer } from './TemplateVersionViewer'

export const CONFIGURATION_VIEWER_ID = 'configurationViewer'

type Props = {
  versionInfo: TemplateVersionInfo
}

export const TemplateCentralView = ({ versionInfo }: Props) => {
  const { getScrollPath, detailsPath } = useTemplateContext()
  const scrollableDivRef = useRef<HTMLDivElement>(null)
  const {
    preferences: {
      templates: { isFlattened, isShowingEmptyValues },
    },
  } = useLayoutContext()

  useEffect(function restoresTemplateScrollPosition() {
    const activeFieldPath = getScrollPath()

    if (detailsPath || !activeFieldPath) {
      return
    }

    const element = document.getElementById(activeFieldPath)

    if (!element) {
      return
    }

    // We need to set a timeout in order to allow the element to be mounted on screen and loaded
    const timeoutId = setTimeout(() => {
      if (scrollableDivRef.current) {
        void scrollUntilElementIsVisible({
          container: scrollableDivRef.current,
          element,
          block: 'end',
          behavior: 'instant',
        })
      }
    }, 200)

    return () => clearTimeout(timeoutId)
    // We only want to restore the scroll position when the table is mounted.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    function ensureDetailsPathIsInView() {
      const isScrollableItem = versionInfo.identifier && detailsPath

      if (!isScrollableItem) {
        return
      }

      const hashedElement = document.getElementById(detailsPath)

      const timeoutId = setTimeout(async () => {
        if (hashedElement?.parentElement && scrollableDivRef.current) {
          await scrollUntilElementIsVisible({
            container: scrollableDivRef.current,
            element: hashedElement.parentElement,
            behavior: 'instant',
          })
        }
      }, 500)

      return () => clearTimeout(timeoutId)
    },
    [isFlattened, isShowingEmptyValues, detailsPath, versionInfo.identifier],
  )

  return (
    <div className="size-full overflow-hidden">
      <TemplateTopBar />
      <div
        id={CONFIGURATION_VIEWER_ID}
        className="relative h-[calc(100%-var(--topBar-height))] overflow-y-auto overflow-x-visible pb-40 pl-2 pr-10"
        ref={scrollableDivRef}
      >
        {versionInfo.isHideVersion ? (
          <HiddenTemplateViewer templateId={versionInfo.identifier} />
        ) : (
          <TemplateVersionViewer />
        )}
      </div>
    </div>
  )
}
